import { shouldPolyfill as shouldPolyfill_2 } from "../vendor/@formatjs/intl-durationformat/should-polyfill.mjs";
import { format, intervalToDuration, formatDuration } from "date-fns";
import { enGB } from "date-fns/locale";
import { ref, toRef, unref } from "vue";
import convert from "convert";
import { watchImmediate } from "@vueuse/core";
import { distanceUnitMapper } from "../utils/distanceUnit.mjs";
import { roundUpToNearestUnit } from "../utils/duration.mjs";
const localeRef = ref("en");
const supportedLocalesConfigRef = ref([]);
function setupLocaleFormat(options) {
  if (options == null ? void 0 : options.supportedLocalesConfig) {
    watchImmediate(
      toRef(options == null ? void 0 : options.supportedLocalesConfig),
      (newSupportedLocalesConfig) => {
        supportedLocalesConfigRef.value = newSupportedLocalesConfig;
      }
    );
  }
  if (options == null ? void 0 : options.locale) {
    watchImmediate(toRef(options == null ? void 0 : options.locale), (newLocale) => {
      localeRef.value = newLocale;
    });
  }
}
function useLocaleFormat(options) {
  setupLocaleFormat(options);
  function getLocaleDateFns(locale) {
    var _a;
    const dateFnsLocale = (_a = supportedLocalesConfigRef.value.find(
      (supportedLocale) => supportedLocale.locale === locale
    )) == null ? void 0 : _a.dateFns;
    return dateFnsLocale ?? enGB;
  }
  function getLocaleFallback(locale) {
    var _a;
    const localeFallback = (_a = supportedLocalesConfigRef.value.find(
      (supportedLocale) => supportedLocale.locale === locale
    )) == null ? void 0 : _a.fallback;
    return localeFallback ?? "en";
  }
  function getSupportedNumberFormatLocale(locale) {
    const isLocaleSupported = Intl.NumberFormat.supportedLocalesOf(locale).length > 0;
    if (isLocaleSupported) return locale;
    return getLocaleFallback(locale);
  }
  function formatDate(date, options2) {
    const { format: format$1 = "yyyy-MM-dd" } = options2 ?? {};
    if (date === null || date === void 0 || date == "") return "";
    const _date = typeof date === "string" ? new Date(date) : date;
    return format(_date, format$1, {
      locale: getLocaleDateFns(localeRef.value)
    });
  }
  function formatDuration$1(duration, options2) {
    const style = (options2 == null ? void 0 : options2.style) ?? "long";
    if (style !== "long" && shouldPolyfill_2()) {
      throw new Error(
        "Intl.DurationFormat is not supported, please call `loadPolyfills()` method before."
      );
    }
    const { format: format2 = ["minutes"], roundUp = null } = options2 ?? {};
    if (duration === null || duration === void 0) return "";
    const durationInSeconds = typeof duration === "string" ? Number(duration) : duration;
    const durationInMs = (roundUp ? roundUpToNearestUnit(durationInSeconds, roundUp) : durationInSeconds) * 1e3;
    const durationObject = intervalToDuration({
      start: 0,
      end: durationInMs
    });
    const formats = [
      "years",
      "months",
      "weeks",
      "days",
      "hours",
      "minutes",
      "seconds"
    ];
    for (const _format of formats) {
      !format2.includes(_format) && (durationObject[_format] = 0);
    }
    if (style === "long" && shouldPolyfill_2()) {
      return formatDuration(durationObject, {
        format: format2,
        locale: getLocaleDateFns(localeRef.value)
      });
    }
    return new Intl.DurationFormat(localeRef.value, {
      style
    }).format(durationObject);
  }
  function formatDistance(distance, options2) {
    const {
      inputUnit = "km",
      outputUnit = "km",
      maximumFractionDigits = 2
    } = options2 ?? {};
    const _distance = convert(Number(distance), inputUnit).to(
      outputUnit
    );
    return new Intl.NumberFormat(
      getSupportedNumberFormatLocale(localeRef.value),
      {
        style: "unit",
        unit: distanceUnitMapper[outputUnit],
        maximumFractionDigits
      }
    ).format(_distance);
  }
  function formatNumber(number) {
    return new Intl.NumberFormat(
      getSupportedNumberFormatLocale(localeRef.value)
    ).format(unref(number));
  }
  function formatPercent(number) {
    return new Intl.NumberFormat(
      getSupportedNumberFormatLocale(localeRef.value),
      {
        style: "unit",
        unit: "percent"
      }
    ).format(unref(number));
  }
  function formatPrice(price, options2) {
    const {
      currency = "EUR",
      zeroLabel,
      minimumFractionDigits = 0
    } = options2 ?? {};
    const priceInBasicUnit = (unref(price) ?? 0) / 100;
    if (zeroLabel && priceInBasicUnit === 0) return zeroLabel;
    return new Intl.NumberFormat(
      getSupportedNumberFormatLocale(localeRef.value),
      {
        style: "currency",
        ...options2,
        minimumFractionDigits,
        currency: unref(currency)
      }
    ).format(priceInBasicUnit);
  }
  async function loadPolyfills() {
    if (shouldPolyfill_2()) {
      await import("../vendor/@formatjs/intl-durationformat/polyfill.mjs").then((n) => n.p);
    }
  }
  return {
    setupLocaleFormat,
    formatDate,
    formatDistance,
    formatDuration: formatDuration$1,
    formatNumber,
    formatPercent,
    formatPrice,
    loadPolyfills
  };
}
export {
  useLocaleFormat
};
